import React, { useEffect, useRef, useCallback } from 'react';

/**
 * GoogleAuthButton Component
 *
 * Renders Google's official sign-in button using Google Identity Services
 * Uses renderButton() method which opens a popup for account selection
 * This approach is more reliable than One Tap and works in incognito mode
 *
 * Props:
 *   - onSuccess: Callback function called with Google credential token on success
 *   - onError: Callback function called with error message on failure
 *
 * Flow:
 *   1. Load Google Identity Services script
 *   2. Initialize Google accounts with Client ID
 *   3. Render Google's official button in the container div
 *   4. On click, popup opens for account selection
 *   5. On selection, receive credential token
 *   6. Pass token to parent component via onSuccess callback
 */
function GoogleAuthButton({ onSuccess, onError }) {
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  // Google Client ID from environment variables
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  // Memoize the callback to prevent unnecessary re-renders
  const handleGoogleResponse = useCallback((response) => {
    if (response.credential) {
      // Successfully received Google credential token
      if (onSuccess) {
        onSuccess(response.credential);
      }
    } else {
      // Failed to receive credential
      const errorMsg = 'Failed to receive Google credential';
      console.error(errorMsg);
      if (onError) {
        onError(errorMsg);
      }
    }
  }, [onSuccess, onError]);

  useEffect(() => {
    // Check if Google Client ID is configured
    if (!GOOGLE_CLIENT_ID) {
      console.error('Google Client ID is not configured');
      if (onError) {
        onError('Google authentication is not configured');
      }
      return;
    }

    // Load Google Identity Services script and initialize
    const initializeGoogle = () => {
      if (window.google && window.google.accounts && buttonRef.current && containerRef.current) {
        try {
          // Initialize Google Identity Services
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleGoogleResponse,
            auto_select: false,
            cancel_on_tap_outside: true,
          });

          // Measure container width and clamp to Google's 200-400px range
          const containerWidth = containerRef.current.offsetWidth;
          const buttonWidth = Math.max(200, Math.min(400, containerWidth));

          // Render Google's official button
          window.google.accounts.id.renderButton(
            buttonRef.current,
            {
              type: 'standard',
              theme: 'outline',
              size: 'large',
              text: 'continue_with',
              shape: 'rectangular',
              width: buttonWidth,
              logo_alignment: 'left',
            }
          );
        } catch (error) {
          console.error('Error initializing Google Identity Services:', error);
          if (onError) {
            onError('Failed to initialize Google authentication');
          }
        }
      }
    };

    // Check if script already loaded
    if (window.google && window.google.accounts) {
      initializeGoogle();
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      initializeGoogle();
    };

    script.onerror = () => {
      console.error('Failed to load Google Identity Services');
      if (onError) {
        onError('Failed to load Google authentication');
      }
    };

    document.body.appendChild(script);

    // Cleanup
    return () => {
      // Note: We don't remove the script as it may be used by other components
    };
  }, [GOOGLE_CLIENT_ID, handleGoogleResponse, onError]);

  // Don't render if Google Client ID is not configured
  if (!GOOGLE_CLIENT_ID) {
    return null;
  }

  return (
    <div ref={containerRef} className="w-full">
      {/* Google will render its button inside this div */}
      <div ref={buttonRef} className="google-signin-button"></div>
    </div>
  );
}

export default GoogleAuthButton;
